import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { Link } from 'react-router-dom'
import Box from '@mui/material/Box'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import { useTheme } from '@mui/material/styles'
import { FeatureConfig } from './types'

export interface NewFeatureModalProps extends FeatureConfig {
  open: boolean
  onClose: () => void
  'data-testid'?: string
}

const LearnMoreLink = ({
  learnMoreLink,
}: {
  learnMoreLink: string | undefined
}) => {
  const theme = useTheme()
  if (!learnMoreLink) return null
  return (
    <Link
      to={learnMoreLink}
      rel="noreferrer"
      style={{ color: theme.palette.primary.main, textDecoration: 'underline' }}
      target="_blank"
    >
      Learn more
    </Link>
  )
}

const NewFeatureModal = ({
  open,
  onClose,
  title,
  header,
  body,
  primaryLabel = 'Continue',
  primaryAction,
  secondaryLabel,
  secondaryAction,
  learnMoreLink,
  'data-testid': testId = 'feature-announcement-modal',
}: NewFeatureModalProps) => {
  const theme = useTheme()
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      data-testid={testId}
    >
      <DialogContent sx={{ p: 3, position: 'relative' }}>
        {header && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            {header}
            <IconButton
              onClick={onClose}
              aria-label="close"
              data-testid="feature-announcement-close-button"
              sx={{
                position: 'absolute',
                top: theme.spacing(2),
                right: theme.spacing(2),
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}

        <Typography
          variant="caption"
          sx={{
            bgcolor: theme.palette.success.lightest,
            color: theme.palette.success.dark,
            px: 1,
            py: 0.5,
            borderRadius: '25px',
            mb: 1.5,
            display: 'inline-block',
            position: 'relative',
          }}
        >
          NEW FEATURE
        </Typography>

        {typeof title === 'string' ? (
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 'bold', mb: 1.5 }}
          >
            {title}
          </Typography>
        ) : null}

        {typeof body === 'string' ? (
          <Typography variant="body1" sx={{ mb: 3 }}>
            {body} <LearnMoreLink learnMoreLink={learnMoreLink} />
          </Typography>
        ) : (
          <Box sx={{ mb: 3 }}>
            {body} <LearnMoreLink learnMoreLink={learnMoreLink} />
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {secondaryLabel && (
            <Button
              color="primary"
              size="large"
              onClick={() => {
                if (secondaryAction) secondaryAction()
                else onClose()
              }}
              data-testid="feature-announcement-secondary-button"
            >
              {secondaryLabel}
            </Button>
          )}
          {primaryLabel && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                if (primaryAction) primaryAction()
                onClose()
              }}
              data-testid="feature-announcement-primary-button"
            >
              {primaryLabel}
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default NewFeatureModal
